import { apiClient } from './client';
import type { Appointment } from '@/types';

// Query params for listing appointments
export interface AppointmentsQueryParams {
  page?: number;
  limit?: number;
  from?: string; // ISO date
  to?: string; // ISO date
  clinicId?: string;
  doctorId?: string;
  patientId?: string;
}

// Create appointment data
export interface CreateAppointmentData {
  clinicId: string;
  doctorId: string;
  patientId?: string;
  title: string;
  description?: string;
  startUtc: string; // ISO datetime
  endUtc: string; // ISO datetime
  isAllDay?: boolean;
}

// Update appointment data
export interface UpdateAppointmentData {
  clinicId?: string;
  doctorId?: string;
  patientId?: string;
  title?: string;
  description?: string;
  startUtc?: string;
  endUtc?: string;
  isAllDay?: boolean;
}

// Paginated response
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const appointmentsService = {
  // Get all appointments with pagination and filters
  async getAll(params?: AppointmentsQueryParams): Promise<PaginatedResponse<Appointment>> {
    const response = await apiClient.get('/appointments', { params });
    return response.data;
  },

  // Get single appointment by ID
  async getById(id: string): Promise<Appointment> {
    const response = await apiClient.get(`/appointments/${id}`);
    return response.data;
  },

  // Create new appointment
  async create(data: CreateAppointmentData): Promise<Appointment> {
    const response = await apiClient.post('/appointments', data);
    return response.data;
  },

  // Update appointment
  async update(id: string, data: UpdateAppointmentData): Promise<Appointment> {
    const response = await apiClient.put(`/appointments/${id}`, data);
    return response.data;
  },

  // Delete appointment
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/appointments/${id}`);
  },

  // Complete appointment
  async complete(id: string): Promise<Appointment> {
    const response = await apiClient.post(`/appointments/${id}/complete`);
    return response.data;
  },

  // Cancel appointment
  async cancel(id: string, reason: string): Promise<Appointment> {
    const response = await apiClient.post(`/appointments/${id}/cancel`, { reason });
    return response.data;
  },

  // Reschedule appointment
  async reschedule(
    id: string,
    data: {
      reason: string;
      newStartUtc: string;
      newEndUtc: string;
    }
  ): Promise<{ message: string; oldAppointmentId: string; newAppointment: Appointment }> {
    const response = await apiClient.post(`/appointments/${id}/reschedule`, data);
    return response.data;
  },
};
